import { type FC, useEffect } from "react";

import { DropdownInput, Input, Option, Status } from "@snapchat/snap-design-system";
import { Field, Formik, useFormikContext } from "formik";
import { useIntl } from "react-intl";

import { MyLensesLensCtaType } from "../../../gql-generated/types";
import { formField } from "../../../shared/styles";
import { formikAdapter } from "../../../shared/utils/formik";
import { deeplinkDefault, websiteDefault } from "../../models";
import { type EditLensCtaFormValues } from "../../validation";

import {
    CtaTypeLabel,
    FormTooltipAndroidAppUrl,
    FormTooltipAppName,
    FormTooltipIosAppId,
    FormTooltipText,
    FormTooltipType,
    FormTooltipUri,
    FormTooltipUrl,
    messages,
} from "./messages.generated";
import { options, websiteTextOptions } from "./selectOptions";

const {
    formLabelAndroidAppUrl,
    formLabelAppName,
    formLabelIosAppId,
    formLabelText,
    formLabelUri,
    formLabelType,
    formLabelUrl,
    formPlaceholderAppName,
    formPlaceholderAndroidAppUrl,
    formPlaceholderIosAppId,
    formPlaceholderUri,
    formPlaceholderUrl,
} = messages;

const FormikDropdownInput = formikAdapter(DropdownInput);
const FormikInput = formikAdapter(Input);

export const EditLensCtaFormik = Formik<EditLensCtaFormValues>;

export interface EditLensCtaFormProps {
    showErrors: boolean;
    setShowErrors: (showErrors: boolean) => void;
}
export const EditLensCtaForm: FC<EditLensCtaFormProps> = ({ showErrors, setShowErrors }) => {
    const { values, errors, touched, setFieldValue } = useFormikContext<EditLensCtaFormValues>();

    const intl = useIntl();

    const textError = (!!touched.text || showErrors) && errors.text;
    const typeErorr = (!!touched.type || showErrors) && errors.type;

    const appNameError = (!!touched.deeplink?.appName || showErrors) && errors.deeplink?.appName;
    const uriError = (!!touched.deeplink?.uri || showErrors) && errors.deeplink?.uri;
    const iosAppIdError = (!!touched.deeplink?.iosAppId || showErrors) && errors.deeplink?.iosAppId;
    const androidAppUrlError = (!!touched.deeplink?.androidAppUrl || showErrors) && errors.deeplink?.androidAppUrl;
    const urlError = (!!touched.website?.url || showErrors) && errors.website?.url;

    const typeValue = values.type;

    useEffect(() => {
        setShowErrors(false);
        if (typeValue === MyLensesLensCtaType.CtaTypeUnset) {
            void setFieldValue("deeplink", deeplinkDefault);
            void setFieldValue("website", websiteDefault);
        } else if (typeValue === MyLensesLensCtaType.CtaTypeDeeplink) {
            void setFieldValue("website", websiteDefault);
        } else {
            void setFieldValue("deeplink", deeplinkDefault);
        }
    }, [typeValue, setFieldValue, setShowErrors]);

    if (values.type === MyLensesLensCtaType.CtaTypeUnset) {
        return (
            <Field
                id="lens-cta-type"
                name="type"
                component={FormikDropdownInput}
                label={intl.formatMessage(formLabelType)}
                tooltip={<FormTooltipType />}
                status={typeErorr}
                statusType={typeErorr && Status.ERROR}
            >
                {options.type.map(({ value, label }) => (
                    <Option key={value} value={value}>
                        {label}
                    </Option>
                ))}
            </Field>
        );
    }

    if (values.type === MyLensesLensCtaType.CtaTypeDeeplink) {
        return (
            <>
                <Field
                    className={formField}
                    id="lens-cta-type"
                    name="type"
                    component={FormikDropdownInput}
                    label={intl.formatMessage(formLabelType)}
                    tooltip={<FormTooltipType />}
                    status={typeErorr}
                    statusType={typeErorr && Status.ERROR}
                >
                    {options.type.map(({ value, label }) => (
                        <Option key={value} value={value}>
                            {label}
                        </Option>
                    ))}
                </Field>

                <Field
                    className={formField}
                    id="lens-cta-app-name"
                    name="deeplink.appName"
                    component={FormikInput}
                    label={intl.formatMessage(formLabelAppName)}
                    tooltip={<FormTooltipAppName />}
                    placeholder={intl.formatMessage(formPlaceholderAppName)}
                    status={appNameError}
                    statusType={appNameError && Status.ERROR}
                />

                <Field
                    className={formField}
                    id="lens-cta-text"
                    name="text"
                    component={FormikDropdownInput}
                    label={intl.formatMessage(formLabelText)}
                    tooltip={<FormTooltipText />}
                    status={textError}
                    statusType={textError && Status.ERROR}
                >
                    {options.text.map(({ value, label }) => (
                        <Option key={value} value={value}>
                            {label}
                        </Option>
                    ))}
                </Field>

                <Field
                    className={formField}
                    id="lens-cta-uri"
                    name="deeplink.uri"
                    component={FormikInput}
                    label={intl.formatMessage(formLabelUri)}
                    tooltip={<FormTooltipUri />}
                    placeholder={intl.formatMessage(formPlaceholderUri)}
                    status={uriError}
                    statusType={uriError && Status.ERROR}
                />

                <Field
                    className={formField}
                    id="lens-cta-ios-app-id"
                    name="deeplink.iosAppId"
                    component={FormikInput}
                    label={intl.formatMessage(formLabelIosAppId)}
                    tooltip={<FormTooltipIosAppId />}
                    placeholder={intl.formatMessage(formPlaceholderIosAppId)}
                    status={iosAppIdError}
                    statusType={iosAppIdError && Status.ERROR}
                />

                <Field
                    className={formField}
                    id="lens-cta-android-app-url"
                    name="deeplink.androidAppUrl"
                    component={FormikInput}
                    label={intl.formatMessage(formLabelAndroidAppUrl)}
                    tooltip={<FormTooltipAndroidAppUrl />}
                    placeholder={intl.formatMessage(formPlaceholderAndroidAppUrl)}
                    status={androidAppUrlError}
                    statusType={androidAppUrlError && Status.ERROR}
                />
            </>
        );
    }

    return (
        <>
            <Field
                className={formField}
                id="lens-cta-type"
                name="type"
                component={FormikDropdownInput}
                label={<CtaTypeLabel />}
                tooltip={<FormTooltipType />}
                status={typeErorr}
                statusType={typeErorr && Status.ERROR}
            >
                {options.type.map(({ value, label }) => (
                    <Option key={value} value={value}>
                        {label}
                    </Option>
                ))}
            </Field>

            <Field
                className={formField}
                id="lens-cta-url"
                name="website.url"
                component={FormikInput}
                label={intl.formatMessage(formLabelUrl)}
                tooltip={<FormTooltipUrl />}
                placeholder={intl.formatMessage(formPlaceholderUrl)}
                status={urlError}
                statusType={urlError && Status.ERROR}
            />

            <Field
                className={formField}
                id="lens-cta-text"
                name="text"
                component={FormikDropdownInput}
                label={intl.formatMessage(formLabelText)}
                tooltip={<FormTooltipText />}
                status={textError}
                statusType={textError && Status.ERROR}
            >
                {websiteTextOptions.map(({ value, label }) => (
                    <Option key={value} value={value}>
                        {label}
                    </Option>
                ))}
            </Field>
        </>
    );
};
